import React, { useEffect, useState } from "react";
import { FileField } from "@pd/pd-ui-kit";
import { Flex } from "antd";
import { Control } from "react-hook-form";
import { Controller } from "react-hook-form/dist/index.ie11";
import { useTranslation } from "react-i18next";

import DeleteIcon from "~/components/Icons/svg/delete";
import RedesignButton from "~/shared/ui/Fields/RedesignButton";
import { TUploadedFile } from "~/client/http/FileController";
import { NOTIFICATIONS } from "~/constans/notifications";
import { uploadFile } from "~/libs/uploadFile";
import { useAddNotificationMutation } from "~/schemas/generated/graphql";

import s from "./FileLoadierField.less";

type TAccept = string[];

interface IFileLoaderFieldProps
  extends Omit<IFileLoaderFieldRawProps, "onChange" | "value"> {
  name: string;
  control: Control;
}

export const FileLoaderField = (props: IFileLoaderFieldProps) => {
  const { name, control, ...etc } = props;

  return (
    <Controller
      name={name}
      control={control}
      render={({ value, onChange }) => (
        <FileLoaderFieldRaw value={value} onChange={onChange} {...etc} />
      )}
    />
  );
};

interface IFileLoaderFieldRawProps {
  value: string[];
  onChange: (fileIds: string[]) => void;
  fileList: TUploadedFile[];
  /**
   * Ограничение на допустимые mime type файлов.
   * Пример: ["application/pdf", "image/jpeg"]
   * Расширения (".pdf" и т.п.) игнорируются — FileField сравнивает
   * file.type со строками accept строго.
   */
  accept?: TAccept;
  /**
   * Ограничение размера файла в мегабайтах.
   * Если файл больше — FileField показывает ошибку под полем.
   */
  maxSizeMb?: number;
  /** Ошибка из формы (react-hook-form/yup) */
  error?: string;
}

export const FileLoaderFieldRaw = (props: IFileLoaderFieldRawProps) => {
  const {
    value = [],
    onChange,
    fileList = [],
    accept,
    maxSizeMb,
    error,
  } = props;
  const { t } = useTranslation();
  const [addNotificationMutation] = useAddNotificationMutation({});
  const fallBack = () => {
    addNotificationMutation({
      variables: {
        type: NOTIFICATIONS.error,
        content: t("common.data_uploaded_error"),
      },
    });
  };

  const [loadingUpload, setLoadingUpload] = useState(false);
  const [loadedFileList, setLoadedFileList] = useState<TUploadedFile[]>([]);
  const [deletedFileIdList, setDeletedFileIdList] = useState<string[]>([]);

  // FileField сравнивает file.type со строками accept строго (только MIME),
  // поэтому расширения вида ".pdf" из accept отбрасываем.
  const mimeAccept = accept?.filter((a) => !a.trim().startsWith("."));

  const handleUpload = async (file: File) => {
    setLoadingUpload(true);
    try {
      const uploaded = await uploadFile(file, fallBack);
      if (uploaded) {
        // Файлов может быть несколько: FileField работает как пикер,
        // id уходит в общий список значения формы (массив fileIds),
        // а само значение FileField остаётся null — кнопка загрузки
        // показывается снова.
        setLoadedFileList((p) => [...p, uploaded]);
      }
    } finally {
      setLoadingUpload(false);
    }
  };

  const handleDelete = (id: string) => () => {
    setDeletedFileIdList((p) => [...p, id]);
    setLoadedFileList((p) => {
      return p.filter((f) => f.id !== id);
    });
  };

  useEffect(() => {
    const filtered = loadedFileList
      .filter((f) => !value.includes(f.id))
      .map((f) => f.id);
    const withExcludedDeletion = [...value, ...filtered].filter(
      (file) => !deletedFileIdList.includes(file)
    );

    onChange(withExcludedDeletion);
  }, [loadedFileList, deletedFileIdList]);

  const filesToView = value
    .reduce((acc, id) => {
      const fileInstance = [...fileList, ...loadedFileList].find(
        (f) => f.id === id
      );
      if (fileInstance) {
        return [...acc, fileInstance];
      }
      return acc;
    }, [])
    .filter((file) => !deletedFileIdList.includes(file.id));

  return (
    <>
      <Flex gap={10} style={{ width: "100%" }} wrap="wrap" vertical>
        {filesToView.map((f) => (
          <Flex key={f.id} style={{ width: "100%" }} justify={"space-between"}>
            <span className={s.uploadFileName}>{f.name}</span>
            <RedesignButton
              txtBtn={t("ui.delete")}
              theme={"empty-accent"}
              customClassName={s.buttonDelete}
              onClick={handleDelete(f.id)}
              iconComponent={
                <DeleteIcon width={14} height={15} fill={"#E37639"} />
              }
            />
          </Flex>
        ))}
      </Flex>
      <FileField
        value={null}
        accept={mimeAccept}
        maxSizeMb={maxSizeMb}
        loading={loadingUpload}
        error={error}
        uploadLabel={t("ui.upload_file")}
        className={s.fileField}
        onUpload={handleUpload}
      />
    </>
  );
};
